import {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
  useId,
} from 'react';
import { SvgIcon } from '../../components/icons';

/**
 * Form controls.
 *
 * Two deliberate differences from the production primitives:
 *
 * 1. **Ids are generated with `useId`.** `src/tailwindcomponents/Input` hardcodes
 *    `id="textfield"` and `aria-describedby="component-helper-text"`, so all 133
 *    instances in the app emit the same DOM ids — clicking a label focuses the
 *    first input on the page rather than its own. Generated ids fix that.
 *
 * 2. **Native `<input type="checkbox|radio">` is used.** The production
 *    Checkbox is a `<span role="checkbox">` with hand-rolled key handling.
 *    Restyling a real input with `appearance: none` gets keyboard support,
 *    form participation and screen-reader semantics for free.
 */

type FieldShellProps = {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  id: string;
  children: ReactNode;
  className?: string;
};

const FieldShell = ({
  label,
  hint,
  error,
  required,
  id,
  children,
  className = '',
}: FieldShellProps) => (
  <div className={className}>
    {label && (
      <label className="ds-label" htmlFor={id}>
        {label}
        {required && (
          <span className="ds-required" aria-hidden="true">
            *
          </span>
        )}
      </label>
    )}
    {children}
    {(error || hint) && (
      <span
        className="ds-hint"
        id={`${id}-hint`}
        data-ds-invalid={error ? 'true' : undefined}
      >
        {error || hint}
      </span>
    )}
  </div>
);

type InputProps = {
  label?: string;
  hint?: string;
  error?: string;
  wrapperClassName?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, wrapperClassName, className = '', id, required, ...rest },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      <input
        ref={ref}
        id={fieldId}
        className={`ds-field ${className}`}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${fieldId}-hint` : undefined}
        {...rest}
      />
    </FieldShell>
  );
});

type TextareaProps = {
  label?: string;
  hint?: string;
  error?: string;
  wrapperClassName?: string;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { label, hint, error, wrapperClassName, className = '', id, required, ...rest },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;

    return (
      <FieldShell
        id={fieldId}
        label={label}
        hint={hint}
        error={error}
        required={required}
        className={wrapperClassName}
      >
        <textarea
          ref={ref}
          id={fieldId}
          className={`ds-field ${className}`}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={error || hint ? `${fieldId}-hint` : undefined}
          {...rest}
        />
      </FieldShell>
    );
  },
);

type SelectProps = {
  label?: string;
  hint?: string;
  error?: string;
  options: Array<{ value: string; label: string }>;
  wrapperClassName?: string;
} & SelectHTMLAttributes<HTMLSelectElement>;

/**
 * Native `<select>`, restyled. A custom listbox buys nothing here and costs
 * keyboard/AT correctness — and on tablets the native picker is genuinely
 * better than any custom popover.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hint,
    error,
    options,
    wrapperClassName,
    className = '',
    id,
    required,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;

  return (
    <FieldShell
      id={fieldId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      className={wrapperClassName}
    >
      <select
        ref={ref}
        id={fieldId}
        className={`ds-field ${className}`}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? `${fieldId}-hint` : undefined}
        {...rest}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
});

type SearchFieldProps = {
  label?: string;
  wrapperClassName?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(
    { label, wrapperClassName, className = '', id, ...rest },
    ref,
  ) {
    const autoId = useId();
    const fieldId = id ?? autoId;

    return (
      <div className={wrapperClassName}>
        {label && (
          <label className="ds-label" htmlFor={fieldId}>
            {label}
          </label>
        )}
        <div className="ds-search">
          <span className="ds-search-icon">
            <SvgIcon name="search" size={14} />
          </span>
          <input
            ref={ref}
            id={fieldId}
            type="search"
            className={`ds-field ${className}`}
            {...rest}
          />
        </div>
      </div>
    );
  },
);

type ToggleProps = {
  label: ReactNode;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

export const Checkbox = forwardRef<HTMLInputElement, ToggleProps>(
  function Checkbox({ label, className = '', ...rest }, ref) {
    return (
      <label className={`ds-control-row ${className}`}>
        <input ref={ref} type="checkbox" className="ds-checkbox" {...rest} />
        <span>{label}</span>
      </label>
    );
  },
);

export const Radio = forwardRef<HTMLInputElement, ToggleProps>(function Radio(
  { label, className = '', ...rest },
  ref,
) {
  return (
    <label className={`ds-control-row ${className}`}>
      <input ref={ref} type="radio" className="ds-radio" {...rest} />
      <span>{label}</span>
    </label>
  );
});

/**
 * Switch. Still a checkbox underneath — `role="switch"` on a real checkbox
 * keeps native behaviour while announcing on/off rather than checked/unchecked.
 */
export const Switch = forwardRef<HTMLInputElement, ToggleProps>(function Switch(
  { label, className = '', ...rest },
  ref,
) {
  return (
    <label className={`ds-control-row ${className}`}>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        className="ds-switch"
        {...rest}
      />
      <span>{label}</span>
    </label>
  );
});
